/**
 * Milestone billing that is scheduled after the close and not yet invoiced.
 *
 * Why it exists: on a fixed-price project the cost of the work lands in a
 * closed month before the milestone invoice for it does, so the page reads the
 * project worse than it is. This panel is the invoice side of that picture.
 *
 * It is a FORECAST, and everything about it says so: a dashed card, a "Not
 * invoiced" badge on the title, and a caption that it is not in margin. None
 * of its figures feed the header, the range totals or any margin -- and it
 * does no arithmetic of its own. Which lines count, the 90-day window and both
 * totals are the engine's; `basis` carries the full rule set and sits on the
 * caption's tooltip.
 */
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { DataTable, ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { PanelTitle } from "@/components/shared/panel-title";
import { StatusChip } from "@/components/shared/status-chip";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useClientSort } from "@/hooks/use-client-sort";
import { COLUMN_HELP } from "@/lib/column-help";
import { calendarDay, money } from "@/lib/format";
import type {
  AccountScheduledBilling,
  ProjectScheduledBilling,
  ScheduledBillingLine,
  ScheduledBillingTotals,
} from "@/lib/profitability-api";

const NO_LINES: ScheduledBillingLine[] = [];

const CAPTION = "Scheduled, not yet invoiced. Not included in margin or any closed-period total.";

function Shell({
  schedule,
  children,
}: {
  schedule: { basis: string; totals: ScheduledBillingTotals; recurring_lines_excluded: number };
  children: ReactNode;
}) {
  const recurring = schedule.recurring_lines_excluded;
  return (
    <Card flush className="w-full border-dashed bg-muted/20">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <PanelTitle
            className="gap-2"
            help={[
              CAPTION,
              recurring === 0
                ? null
                : recurring === 1
                  ? "1 monthly fee with no end date (on the line or the project) is not shown."
                  : `${recurring} monthly fees with no end date (on the line or the project) are not shown.`,
              schedule.basis,
            ]
              .filter((line) => line !== null)
              .join(" ")}
          >
            Scheduled billing
            <StatusChip tone="neutral">Not invoiced</StatusChip>
          </PanelTitle>
          <dl className="flex gap-6 text-right">
            <div>
              <dt className="text-xs text-muted-foreground">Next 90 days</dt>
              <dd className="text-lg font-semibold tabular-nums">
                {money(schedule.totals.next_90_days)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">All scheduled</dt>
              <dd className="text-lg font-semibold tabular-nums">
                {money(schedule.totals.all_scheduled)}
              </dd>
            </div>
          </dl>
        </div>
      </CardHeader>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

function Unavailable() {
  return (
    <Card className="w-full border-dashed bg-muted/20">
      <CardHeader>
        <PanelTitle>Scheduled billing</PanelTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Scheduled billing is unavailable: the billing schedule in NOS could not be read. The
          figures above are unaffected.
        </p>
      </CardContent>
    </Card>
  );
}

function PastDue({ line }: { line: ScheduledBillingLine }) {
  if (!line.past_scheduled_date) return null;
  return (
    <StatusChip tone="warn" title="The scheduled date has passed and no invoice is recorded yet">
      Past due to invoice
    </StatusChip>
  );
}

function empty(label: string) {
  return (
    <EmptyState
      className="py-8"
      title={`No milestone billing scheduled after ${label}.`}
      description="Nothing on the NOS billing schedule after the close is waiting to be invoiced."
    />
  );
}

function lineValue(line: ScheduledBillingLine, key: string) {
  if (key === "amount") return line.amount;
  if (key === "description") return line.description ?? "";
  return line.date;
}

export function ProjectScheduledBillingPanel({
  schedule,
}: {
  schedule: ProjectScheduledBilling | null | undefined;
}) {
  const sorted = useClientSort(schedule?.lines ?? NO_LINES, lineValue, {
    key: "date",
    dir: "asc",
  });
  if (!schedule) return <Unavailable />;

  return (
    <Shell schedule={schedule}>
      <DataTable
        flush
        headers={[
          { label: "Expected", sortKey: "date", help: COLUMN_HELP.scheduledDate },
          { label: "Milestone", sortKey: "description" },
          {
            label: "Amount",
            className: "text-right",
            sortKey: "amount",
            help: COLUMN_HELP.scheduledAmount,
          },
          { label: "Status", className: "text-right" },
        ]}
        sort={sorted.sort}
        sortDir={sorted.sortDir}
        onSort={sorted.toggleSort}
        empty={empty(schedule.closed_through_label)}
      >
        {sorted.rows.map((line) => (
          <tr key={line.line_id} className={ROW_CLASS}>
            <td className="px-5 py-3 whitespace-nowrap">{calendarDay(line.date)}</td>
            <td className="px-5 py-3">{line.description ?? "—"}</td>
            <td className="px-5 py-3 text-right tabular-nums">{money(line.amount)}</td>
            <td className="px-5 py-3 text-right">
              <PastDue line={line} />
            </td>
          </tr>
        ))}
      </DataTable>
    </Shell>
  );
}

interface AccountLine extends ScheduledBillingLine {
  demand_id: string;
  project_name: string | null;
}

function accountLineValue(line: AccountLine, key: string) {
  if (key === "project") return line.project_name ?? line.demand_id;
  return lineValue(line, key);
}

export function AccountScheduledBillingPanel({
  schedule,
}: {
  schedule: AccountScheduledBilling | null | undefined;
}) {
  const lines: AccountLine[] = (schedule?.projects ?? []).flatMap((p) =>
    p.lines.map((line) => ({ ...line, demand_id: p.demand_id, project_name: p.project_name })),
  );
  const sorted = useClientSort(lines, accountLineValue, { key: "date", dir: "asc" });
  if (!schedule) return <Unavailable />;

  return (
    <Shell schedule={schedule}>
      <DataTable
        flush
        headers={[
          { label: "Expected", sortKey: "date", help: COLUMN_HELP.scheduledDate },
          { label: "Project", sortKey: "project" },
          { label: "Milestone", sortKey: "description" },
          {
            label: "Amount",
            className: "text-right",
            sortKey: "amount",
            help: COLUMN_HELP.scheduledAmount,
          },
          { label: "Status", className: "text-right" },
        ]}
        sort={sorted.sort}
        sortDir={sorted.sortDir}
        onSort={sorted.toggleSort}
        empty={empty(schedule.closed_through_label)}
      >
        {sorted.rows.map((line) => (
          <tr key={line.line_id} className={ROW_CLASS}>
            <td className="px-5 py-3 whitespace-nowrap">{calendarDay(line.date)}</td>
            <td className="px-5 py-3">
              <Link
                to="/dashboard/projects/$demandId"
                params={{ demandId: line.demand_id }}
                className="hover:underline"
              >
                {line.project_name ?? line.demand_id}
              </Link>
            </td>
            <td className="px-5 py-3">{line.description ?? "—"}</td>
            <td className="px-5 py-3 text-right tabular-nums">{money(line.amount)}</td>
            <td className="px-5 py-3 text-right">
              <PastDue line={line} />
            </td>
          </tr>
        ))}
      </DataTable>
    </Shell>
  );
}
